import React from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import './styles.scss';

interface PaymentResultModalProps {
    show: boolean;
    onHide: () => void;
    success: boolean;
    subscriptionId: string;
    status?: string;
    message?: string;
    amount?: number;
    installments?: number;
}

const PaymentResultModal: React.FC<PaymentResultModalProps> = ({
    show,
    onHide,
    success,
    subscriptionId,
    status,
    message,
    amount,
    installments
}) => {
    const formatAmount = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value / 100);
    };

    const getStatusColor = () => {
        if (!status) return success ? 'success' : 'danger';
        
        switch (status.toLowerCase()) {
            case 'active':
            case 'paid':
                return 'success';
            case 'pending':
                return 'warning';
            case 'failed':
            case 'canceled':
                return 'danger';
            default:
                return 'info';
        }
    };

    const getStatusIcon = () => {
        const color = getStatusColor();
        switch (color) {
            case 'success':
                return '✅';
            case 'warning':
                return '⚠️';
            case 'danger':
                return '❌';
            default:
                return 'ℹ️';
        }
    };

    const getStatusText = () => {
        if (!status) return success ? 'Aprovado' : 'Rejeitado';
        
        switch (status.toLowerCase()) {
            case 'active':
                return 'Ativado';
            case 'paid':
                return 'Pago';
            case 'pending':
                return 'Pendente';
            case 'failed':
                return 'Falhou';
            case 'canceled':
                return 'Cancelado';
            default:
                return status;
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered className="payment-result-modal">
            <Modal.Header closeButton className={`bg-${getStatusColor()} text-white`}>
                <Modal.Title>
                    {getStatusIcon()} Resultado do Pagamento
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4 text-center">
                <div className={`status-icon mb-4 text-${getStatusColor()}`}>
                    {success ? (
                        <i className="bi bi-check-circle-fill" style={{ fontSize: '5rem' }}></i>
                    ) : (
                        <i className="bi bi-x-circle-fill" style={{ fontSize: '5rem' }}></i>
                    )}
                </div>

                <h4 className="mb-3">
                    {success ? 'Pagamento Aprovado!' : 'Pagamento Não Aprovado'}
                </h4>

                <Alert variant={getStatusColor()} className="text-start">
                    <p className="mb-1"><strong>Status:</strong> {getStatusText()}</p>
                    <p className="mb-1"><strong>ID da Assinatura:</strong> {subscriptionId}</p>
                    {amount && (
                        <p className="mb-1">
                            <strong>Valor:</strong> {formatAmount(amount)}
                            {installments && installments > 1 && ` (${installments}x de ${formatAmount(amount / installments)})`}
                        </p>
                    )}
                    {message && (
                        <>
                            <hr />
                            <p className="mb-0">{message}</p>
                        </>
                    )}
                </Alert>

                {success ? (
                    <Alert variant="info" className="text-start">
                        <h6 className="alert-heading">🎉 Próximos Passos:</h6>
                        <ul className="mb-0">
                            <li>Sua assinatura foi ativada com sucesso</li>
                            <li>O pagamento será processado automaticamente todo mês</li>
                        </ul>
                    </Alert>
                ) : (
                    <Alert variant="warning" className="text-start">
                        <h6 className="alert-heading">💡 O que fazer:</h6>
                        <ul className="mb-2">
                            <li>Verifique os dados do seu cartão</li>
                            <li>Certifique-se de ter saldo disponível</li>
                            <li>Entre em contato com seu banco se necessário</li>
                            <li>Tente novamente com outro método de pagamento</li>
                        </ul>
                        <hr />
                        <p className="mb-0 small text-muted">
                            <strong>ℹ️ Ambiente de Teste:</strong> Em ambiente de desenvolvimento, 
                            o gateway pode simular rejeições aleatórias para teste de erros. 
                            Em produção, os pagamentos funcionam normalmente.
                        </p>
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Fechar
                </Button>
                {!success && (
                    <Button variant="primary" onClick={onHide}>
                        Tentar Novamente
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default PaymentResultModal;
